import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Pagination, PermissionAssigneeTargetLinkContainerModel, PermissionWizardService, TenantPermissionAssigneeTargetService } from 'blg-akaun-ts-lib';
import { fetchAllByCriteria } from '../../../../listing.utils';
import { ToastrService } from 'ngx-toastr';
import { AppConfig } from '../../../../visa';
import { of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, takeLast } from 'rxjs/operators';
import { PermWizardActions } from '../actions';

@Injectable()
export class PermWizardEffects {

    private readonly apiVisa = AppConfig.apiVisa;

    /**
     * Create new permission set.
     */
    createPermWizard$ = createEffect(() => this.actions$.pipe(
        ofType(PermWizardActions.createPermWizardInit),
        mergeMap(action => this.permWizardService.post(action.permWizard, this.apiVisa)
        .pipe(
            map(_ => {
                this.displayToasterSuccess('Permission wizard created successfully');
                return PermWizardActions.createPermWizardSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(PermWizardActions.createPermWizardFailure({error: err.message}))
            })
        )),
        catchError((err) => {
            this.displayToasterError(err.message);
            return of(PermWizardActions.createPermWizardFailure({error: err.message}))
        })
    ))

    /**
     * Update permission set.
     */
    updatePermWizard$ = createEffect(() => this.actions$.pipe(
        ofType(PermWizardActions.updatePermWizardInit),
        mergeMap(action => this.permWizardService.put(action.permWizard, this.apiVisa)
        .pipe(
            map(_ => {
                this.displayToasterSuccess('Permission set updated successfully');
                return PermWizardActions.updatePermWizardSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(PermWizardActions.updatePermWizardFailure({error: err.message}));
            })
        )),
        catchError((err) => {
            this.displayToasterError(err.message);
            return of(PermWizardActions.updatePermWizardFailure({error: err.message}));
        })
    ));

    /**
     * Update perm assignee target links with new target hdr guids.
     */
    updatePermWizardAssigneeTargetLinks$ = createEffect(() => this.actions$.pipe(
        ofType(PermWizardActions.updatePermAssigneeTargetLinksInit),
        concatMap(action => action.lines),
        concatMap(line => {
            const permWizardHdrGuid = line.hdr_guid;
            const permWizardLineGuid = line.guid;

            const criteria = [
                {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
                {columnName: 'perm_set_hdr_guid', operator: '=', value: permWizardHdrGuid.toString()},
                {columnName: 'perm_set_line_guid', operator: '=', value: permWizardLineGuid.toString()}
            ]

            const pagination = new Pagination(null, null, criteria);

            return this.tenantPermAssigneeTargetLinkService.getByCriteria(pagination, this.apiVisa)
            .pipe(
                concatMap(x => x.data),
                mergeMap((x: PermissionAssigneeTargetLinkContainerModel) => {
                    // update the target hdr guid
                    x.permAssigneeTargetLink.target_hdr_guid = line.target_hdr_guid;

                    // send put request to the new container
                    return this.tenantPermAssigneeTargetLinkService.put(x, this.apiVisa)
                    .pipe(
                        catchError((err) => {
                            this.displayToasterError(err.message);
                            return of(PermWizardActions.updatePermAssigneeTargetLinksFailure({error: err.message}))
                        })
                    )
                }),
                catchError((err) => {
                    this.displayToasterError(err.message);
                    return of(PermWizardActions.updatePermAssigneeTargetLinksFailure({error: err.message}))
                })
            )
        }),
        map((_) => {
            this.displayToasterSuccess('Permission assignee target links updated successfully');
            return PermWizardActions.updatePermAssigneeTargetLinksSuccess();
        }),
        catchError((err) => {
            this.displayToasterError(err.message);
            return of(PermWizardActions.updatePermAssigneeTargetLinksFailure({error: err.message}))
        })
    ))

    /**
     * Delete permission set.
     */
    deletePermWizard$ = createEffect(() => this.actions$.pipe(
        ofType(PermWizardActions.deletePermWizardInit),
        mergeMap(action => this.permWizardService.delete(action.guid, this.apiVisa)
        .pipe(
            map(() => {
                this.displayToasterSuccess('Permission set deleted successfully');
                return PermWizardActions.deletePermWizardSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(PermWizardActions.deletePermWizardFailure({error: err.message}));
            })
        )),
        catchError((err) => {
            this.displayToasterError(err.message);
            return of(PermWizardActions.deletePermWizardFailure({error: err.message}));
        })
    ))

    /**
     * Delete permission assignee target links associated with this
     * permission set guid.
     */
    deletePermAssigneeTargetLinks$ = createEffect(() => this.actions$.pipe(
        ofType(PermWizardActions.deletePermAssigneeTargetLinksInit),
        /**
         * Resolve the links to delete.
         *
         * Replaces the raw SELECT that went through SubQueryService:
         *
         *   SELECT DISTINCT(hdr.guid) FROM app_perm_assignee_target_link hdr
         *   WHERE hdr.perm_set_hdr_guid = '<guid>' AND hdr.status != 'DELETED'
         *
         * GET permission-assignee-target/query ->
         * PermissionAssigneeTargetLinkUow.getByCriteria ->
         * generateWhereClauseByCriteria honours `perm_set_hdr_guid` and defaults to
         * `status != 'DELETED'` when no status is sent, so both halves of the WHERE
         * clause are expressed as criteria. DISTINCT is redundant here: the old
         * query selected the primary key.
         *
         * fetchAllByCriteria pages until the endpoint stops returning full pages,
         * so every matching link is deleted. A single getByCriteria would cap the
         * deletion at one page and leave residual links behind, which the raw
         * subquery never did.
         */
        concatMap(action => fetchAllByCriteria<PermissionAssigneeTargetLinkContainerModel>(
            pagination => this.tenantPermAssigneeTargetLinkService.getByCriteria(pagination, this.apiVisa),
            [{ columnName: 'perm_set_hdr_guid', operator: '=', value: action.permWizardHdrGuid }]
        )),
        map(links => links.map(link => link.permAssigneeTargetLink.guid.toString())),
        filter(res => res.length > 0),
        // return guids
        concatMap(res => res),
        mergeMap(guid => this.tenantPermAssigneeTargetLinkService.delete(guid, this.apiVisa)
        .pipe(
            takeLast(1),
            map(_ => {
                this.displayToasterSuccess('Permission assignee target links deleted successfully');
                return PermWizardActions.deletePermAssigneeTargetLinksSuccess();
            }),
            catchError((err) => {
                this.displayToasterError(err.message);
                return of(PermWizardActions.deletePermAssigneeTargetLinksFailure({error: err.message}));
            })
        )),
        catchError((err) => {
            this.displayToasterError(err.message);
            return of(PermWizardActions.deletePermAssigneeTargetLinksFailure({error: err.message}));
        })
    ))

    constructor(
        private tenantPermAssigneeTargetLinkService: TenantPermissionAssigneeTargetService,
        private permWizardService: PermissionWizardService,
        private actions$: Actions,
        private toaster: ToastrService
    ) {};

    /**
     * Display a successful toaster message.
     *
     * @param message : the message to display
     * @returns the toaster
     */
    displayToasterSuccess(message: string) {
        return this.toaster.success(
            message,
            'Success',
            {
                tapToDismiss: true,
                progressBar: true,
                timeOut: 1300
            }
        )
    }

    /**
     * Display a toaster when there is an error.
     *
     * @param message : the messsage to display
     * @returns the toaster
     */
    displayToasterError(message: string) {
        return this.toaster.error(
            message,
            'Error',
            {
                tapToDismiss: true,
                progressBar: true,
                timeOut: 1300
            }
        )
    }

}
